import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface StartGuideTasksBannerProps {
  shouldShowStartGuideBanner: boolean;
  isAdmin: boolean;
  onDismiss: () => void;
  onOpenP4: () => void;
}

/*
 * Style.
 */

const StyledBannerIcon = styled.div`
  grid-area: icon;
  margin: 2px 0;
`;

const StyledBannerCloseAction = styled(BannerCloseAction)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledBannerContent = styled(BannerContent)`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledBannerText = styled.div`
  position: relative;
  grid-area: content;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

const StyledBannerAction = styled(BannerAction)`
  color: ${(p) => p.theme.banner.action};
  &:hover {
    background: ${(p) => p.theme.palette.green.shade30};
  }
`;

/*
 * Component.
 */

export const StartGuideTasksBanner: FC<StartGuideTasksBannerProps> = (props) => {
  const theme = useUserTheme();
  useInitialEffect(() => {
    if (props.shouldShowStartGuideBanner) {
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_P1_REMINDER_VIEW, {
        type: 'getting_started',
        user_track: props.isAdmin ? 'admin' : 'non_admin',
      });
    }
  });

  if (!props.shouldShowStartGuideBanner) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.green.shade20}
      priority={BannerPrioritiesEnum.TRANSIENT}
    >
      <StyledBannerCloseAction onClick={props.onDismiss} />
      <StyledBannerIcon>
        <Icon name="lightbulbSmall" color={theme.palette.green.shade40} />
      </StyledBannerIcon>
      <StyledBannerContent>
        <BannerContentTitle>
          <Trans id="start-guide-banner-title">There&apos;s more left to learn</Trans>
        </BannerContentTitle>
        <StyledBannerText>
          <Trans id="start-guide-banner-description">
            To get the most out of Front, make sure you finish learning how it works.
          </Trans>
        </StyledBannerText>
      </StyledBannerContent>
      <BannerFooterActions>
        <StyledBannerAction onClick={props.onOpenP4}>
          <Trans id="start-guide-banner-action">Finish your to-dos</Trans>
        </StyledBannerAction>
      </BannerFooterActions>
    </Banner>
  );
};
